/**
 * usePageTracking Hook
 *
 * Automatically tracks `page.viewed` events on route changes.
 * - Web: listens to React Router's `useLocation()`
 * - Plugin: listens to NavigationContext `currentPage` from Redux
 *
 * Place this hook once in the root App component.
 *
 * @module @ideoz/core/analytics/hooks/usePageTracking
 */

import { useEffect, useRef } from 'react';
import { isWebBuild } from '../../utils/environment';
import { analyticsConfig } from '../analytics.config';
import { trackEvent } from '../analytics.service';

/**
 * Match a pathname against the pageViews config.
 */
function resolvePageName(pathname: string): string | undefined {
  const config = analyticsConfig.pageViews;

  // Exact match first
  if (config[pathname]) return config[pathname].page_name;

  // Pattern match
  for (const pattern of Object.keys(config)) {
    if (matchRoute(pattern, pathname)) {
      return config[pattern].page_name;
    }
  }
  return undefined;
}

function matchRoute(pattern: string, pathname: string): boolean {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');

  for (let i = 0; i < patternParts.length; i++) {
    const pp = patternParts[i];
    if (pp === '*') return true;
    if (pp.startsWith(':')) {
      if (!pathParts[i]) return false;
      continue;
    }
    if (pp !== pathParts[i]) return false;
  }
  return patternParts.length === pathParts.length;
}

// ==================== Web Hook ====================

/**
 * Track page views for the web app.
 * Must be called inside a React Router context.
 *
 * @param pathname - Current location.pathname from useLocation()
 */
export function useWebPageTracking(pathname: string): void {
  const prevPath = useRef<string | null>(null);

  useEffect(() => {
    if (pathname === prevPath.current) return;
    prevPath.current = pathname;

    const pageName = resolvePageName(pathname);
    if (pageName) {
      trackEvent('page_viewed', {
        page_name: pageName,
        page_path: pathname,
      });
    }
  }, [pathname]);
}

// ==================== Plugin Hook ====================

/**
 * Track page views for the Figma plugin.
 * Listens to the `currentPage` value (from Redux or NavigationContext).
 *
 * @param currentPage - The current page identifier in the plugin
 */
export function usePluginPageTracking(currentPage: string | undefined): void {
  const prevPage = useRef<string | null>(null);

  useEffect(() => {
    if (!currentPage || currentPage === prevPage.current) return;
    prevPage.current = currentPage;

    const pageName = resolvePageName(currentPage);
    if (pageName) {
      trackEvent('page.viewed', {
        page_name: pageName,
        page_path: currentPage,
      });
    }
  }, [currentPage]);
}
